import React, { useEffect, useRef, useState } from 'react';
import { getHarvestConfig, updateRecord } from '../../services/harvestingService';
import HarvestForm from './HarvestForm';
import '../../pages/AccountPage.css';

export default function HarvestRecordEditor({ tenantId, record, onSaved, onCancel, onBusyChange }) {
  const [taxonomy, setTaxonomy] = useState(null);
  const [revision, setRevision] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let current = true;
    setError('');
    getHarvestConfig(tenantId).then(({ data }) => {
      if (!Array.isArray(data?.categories) || !data.categories.length) throw new Error('Unable to read harvest setup.');
      if (current) setTaxonomy(data);
    }).catch((err) => {
      if (current) setError(err.response?.data?.message || err.message || 'Unable to load harvest setup.');
    });
    return () => { current = false; };
  }, [tenantId, revision]);
  const save = async (values) => {
    setSaving(true);
    onBusyChange?.(true);
    setError('');
    try {
      const payload = { ...values };
      if (values.speciesId === (record.speciesId?._id || record.speciesId || '')
        && values.species === (record.species || '')) {
        delete payload.species;
        delete payload.speciesId;
      }
      const { data } = await updateRecord(tenantId, record._id, payload);
      onSaved(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save your harvest record. Please retry.');
    } finally {
      setSaving(false);
      onBusyChange?.(false);
    }
  };
  const initial = {
    ...Object.fromEntries(['category', 'subcategory', 'species', 'season', 'unit', 'abundance', 'locationDescription',
      'observations', 'traditionalKnowledge', 'locationPrecision'].map((key) => [key, record[key] || ''])),
    speciesId: record.speciesId?._id || record.speciesId || '',
    harvestDate: record.harvestDate ? record.harvestDate.slice(0, 10) : '',
    quantity: record.quantity ?? '',
    latitude: record.location?.coordinates?.[1] ?? '',
    longitude: record.location?.coordinates?.[0] ?? '',
    isTraditionalUse: !!record.isTraditionalUse,
    mediaUrlsText: (record.mediaUrls || []).join('\n'),
    harvestDetails: record.harvestDetails || {},
  };
  return <div className="my-data-edit">
    <p>Edit your harvest information below. Community, record ownership and staff audit information cannot be changed.</p>
    {!taxonomy ? <>
      {error ? <div role="alert">{error}<button type="button" onClick={() => setRevision((value) => value + 1)}>Retry harvest setup</button></div>
        : <p role="status">Loading harvest setup...</p>}
      <button type="button" onClick={onCancel}>Cancel</button>
    </> : <fieldset disabled={saving} className="my-data-edit__fields">
      <legend>Edit harvest record</legend>
      <HarvestForm editMode tenantId={tenantId} taxonomy={taxonomy} initial={initial}
        loading={saving} error={error} onSubmit={save} onCancel={onCancel} />
    </fieldset>}
  </div>;
}

export function HarvestRecordEditDialog({ tenantId, record, onSaved, onCancel }) {
  const dialogRef = useRef(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog.showModal();
    return () => { dialog.close(); };
  }, []);
  return <dialog ref={dialogRef} className="harvest-record-edit-dialog" aria-label="Edit harvest point"
    onCancel={(event) => { event.preventDefault(); if (!busy) onCancel(); }}>
    <h2>Edit harvest point</h2>
    <p>Save or cancel to return to your harvesting map.</p>
    <HarvestRecordEditor tenantId={tenantId} record={record} onSaved={onSaved} onCancel={onCancel} onBusyChange={setBusy} />
  </dialog>;
}
